import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { OrgDetails } from '@/components/admin/OrgDetails';
import { apiFetch } from '@/lib/apiFetch';
import { AdminPageShell } from '../../_components/AdminPageShell';
import type { Organization } from '@/types/database';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const OrgDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [org, setOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadOrg = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/organizations/${id}`);
      if (!res.ok) {
        if (res.status === 404) throw new Error('Organization not found');
        throw new Error(`Failed to load organization: ${res.status}`);
      }
      const data = await res.json();
      setOrg(data as Organization);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load organization'
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadOrg();
  }, [loadOrg]);

  if (loading) {
    return (
      <AdminPageShell title="Organization" hideNav>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400 mr-2" />
          <span className="text-gray-500">Loading organization…</span>
        </div>
      </AdminPageShell>
    );
  }

  if (error || !org) {
    return (
      <AdminPageShell title="Organization" hideNav>
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-8 text-center">
          <p className="text-red-600 mb-4">
            {error ?? 'Organization not found'}
          </p>
          <Button
            variant="outline"
            onClick={() => navigate('/admin/organizations')}
          >
            Back to Organizations
          </Button>
        </div>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell title={org.name} hideNav>
      <div className="mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/organizations')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Organizations
        </Button>
      </div>
      <OrgDetails organization={org} onRefresh={loadOrg} />
    </AdminPageShell>
  );
};
